import React, { useState, useEffect, useRef } from 'react';

const NewsSection = () => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [linkHovered, setLinkHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const sectionRef = useRef(null);
  
  // Check if element is in viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // When the section comes into view, set isVisible to true
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Once animation has started, we can stop observing
          observer.unobserve(entry.target);
        }
      },
      {
        root: null, // viewport
        rootMargin: '0px',
        threshold: 0.15 // Trigger when at least 15% of the element is visible
      }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    // Handle window resize
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup observer and event listener on component unmount
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Determine grid layout based on screen size
  const getGridColumns = () => {
    if (windowWidth < 640) {
      return 'repeat(1, 1fr)'; // Single column on mobile
    } else if (windowWidth < 1024) {
      return 'repeat(2, 1fr)'; // Two columns on tablet
    } else {
      return 'repeat(3, 1fr)'; // Three columns on desktop
    }
  };

  // Get image height based on screen size and isLarge property
  const getImageHeight = (isLarge) => {
    if (windowWidth < 640) {
      return '200px'; // Smaller images on mobile
    } else if (windowWidth < 1024) {
      return isLarge ? '300px' : '220px'; // Medium images on tablet
    } else {
      return isLarge ? '350px' : '250px'; // Larger images on desktop
    }
  };

  // Determine heading font size based on screen size
  const getHeadingSize = () => {
    if (windowWidth < 640) {
      return '24px';
    } else if (windowWidth < 1024) {
      return '28px';
    } else {
      return '32px';
    }
  };

  // Plastic Analytics color scheme
  const colors = {
    darkBlue: '#111d4a',
    lightBlue: '#e0e0ff',
    orange: '#e67e22',
    white: '#ffffff',
    lightGray: '#f8f9fa',
    gray: '#6B7280'
  };

  const styles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: windowWidth < 640 ? '30px 16px' : '40px 20px',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
      transition: 'opacity 0.8s ease, transform 0.8s ease'
    },
    header: {
      display: 'flex',
      flexDirection: windowWidth < 640 ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: windowWidth < 640 ? 'flex-start' : 'center',
      gap: windowWidth < 640 ? '12px' : '0',
      marginBottom: windowWidth < 640 ? '24px' : '40px',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
      transition: 'opacity 0.5s ease 0.3s, transform 0.5s ease 0.3s'
    },
    heading: {
      fontSize: getHeadingSize(),
      fontWeight: '600',
      color: colors.darkBlue,
      lineHeight: '1.2',
      marginBottom: windowWidth < 640 ? '8px' : '0'
    },
    seeAllLink: {
      display: 'flex',
      alignItems: 'center',
      color: colors.darkBlue,
      fontWeight: '500',
      fontSize: '16px',
      textDecoration: 'none',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateX(0)' : 'translateX(-10px)',
      transition: 'color 0.3s ease, opacity 0.5s ease 0.6s, transform 0.5s ease 0.6s'
    },
    arrow: {
      marginLeft: '8px',
      transform: linkHovered ? 'translateX(4px)' : 'translateX(0)',
      transition: 'transform 0.3s ease'
    },
    newsGrid: {
      display: 'grid',
      gridTemplateColumns: getGridColumns(),
      gap: windowWidth < 640 ? '20px' : '24px'
    },
    newsItem: (index, isHovered) => ({
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      opacity: isVisible ? 1 : 0,
      transform: isVisible 
        ? (isHovered && windowWidth >= 1024 ? 'translateY(0) scale(1.03)' : 'translateY(0) scale(1)')
        : 'translateY(30px) scale(1)',
      transition: `opacity 0.5s ease ${0.6 + index * 0.15}s, transform 0.5s ease ${0.6 + index * 0.15}s`,
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: isHovered && windowWidth >= 1024 ? colors.lightGray : 'transparent',
      boxShadow: isHovered && windowWidth >= 1024 ? '0 10px 25px rgba(0, 0, 0, 0.08)' : '0 0 0 rgba(0, 0, 0, 0)',
      transitionProperty: 'opacity, transform, box-shadow, background-color',
      transitionDuration: '0.5s, 0.5s, 0.3s, 0.3s',
      transitionTimingFunction: 'ease',
      padding: isHovered && windowWidth >= 1024 ? '16px' : '0',
      margin: isHovered && windowWidth >= 1024 ? '-16px' : '0'
    }),
    largeNewsItem: {
      gridColumn: windowWidth < 640 ? 'auto' : windowWidth < 1024 ? '1 / span 2' : '1 / span 1',
      gridRow: windowWidth < 640 ? 'auto' : windowWidth < 1024 ? '1 / span 1' : '1 / span 2'
    },
    imageContainer: {
      overflow: 'hidden',
      borderRadius: '8px',
      marginBottom: '16px',
      boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${colors.lightGray}`
    },
    newsImage: (isHovered, isLarge) => ({
      width: '100%',
      height: getImageHeight(isLarge),
      objectFit: 'cover',
      transform: isHovered && windowWidth >= 1024 ? 'scale(1.05)' : 'scale(1)',
      transition: 'transform 0.5s ease'
    }),
    newsTitle: (isHovered) => ({
      fontSize: windowWidth < 640 ? '18px' : '20px',
      fontWeight: '600',
      color: isHovered && windowWidth >= 1024 ? colors.orange : colors.darkBlue,
      marginBottom: '12px',
      lineHeight: '1.3',
      transition: 'color 0.3s ease'
    }),
    timestamp: (isHovered) => ({
      fontSize: '14px',
      color: colors.gray,
      opacity: isHovered && windowWidth >= 1024 ? 1 : 0.8,
      transition: 'opacity 0.3s ease',
      display: 'flex',
      alignItems: 'center'
    }),
    timeIcon: {
      marginRight: '6px',
      opacity: 0.7
    }
  };

  // Sample news data
  const newsItems = [
    {
      id: 1,
      title: 'PlasticAnalytics is officially Open-source',
      image: 'https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Fnews%2Fofficially-open-source.jpeg?alt=media&token=cf6c2674-511f-4804-bda8-972966e42326',
      timestamp: '01.30.25',
      isLarge: true
    },
    {
      id: 2,
      title: 'Automatic reference matched reporting',
      image: 'https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Fnews%2Fauto-reporting.png?alt=media&token=135b66e3-536d-4b15-b047-a6303ae8f615',
      timestamp: '01.24.25'
    },
    {
      id: 3,
      title: 'Explore hosted plastic spectral data today!',
      image: 'https://firebasestorage.googleapis.com/v0/b/plasticanalytics-ada31.appspot.com/o/landingPage%2Fnews%2Fexplore-feature.jpeg?alt=media&token=ca79f03c-30d4-4aea-98f9-805733efcd0f',
      timestamp: '01.20.25'
    }
  ];

  return (
    <div ref={sectionRef} style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.heading}>Learn More About Plastic Analytics</h2>
        <a 
          href="/news" 
          style={styles.seeAllLink}
          onMouseEnter={() => setLinkHovered(true)}
          onMouseLeave={() => setLinkHovered(false)}
        >
          See All News
          <span style={styles.arrow}>→</span>
        </a>
      </div>

      <div style={styles.newsGrid}>
        {newsItems.map((item, index) => {
          const isHovered = hoveredItem === item.id;
          
          return (
            <div 
              key={item.id} 
              style={{
                ...styles.newsItem(index, isHovered),
                ...(item.isLarge && windowWidth >= 640 ? styles.largeNewsItem : {})
              }}
              onMouseEnter={() => setHoveredItem(item.id)}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={() => window.location.href = `/news/${item.id}`}
            >
              <div style={styles.imageContainer}>
                <img 
                  src={item.image} 
                  alt={item.title}
                  style={styles.newsImage(isHovered, item.isLarge)}
                />
              </div>
              <h3 style={styles.newsTitle(isHovered)}>{item.title}</h3>
              <span style={styles.timestamp(isHovered)}>
                <svg 
                  style={styles.timeIcon} 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
                  <path d="M12 7v5h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {item.timestamp}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NewsSection;